import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as Sentry from "@sentry/react";
import { useHistory } from "react-router";

import { useAuthContext } from "~/v2/providers/AuthProvider";
import useGetLiveAccounts from "../api/hooks/useGetLiveAccounts";
import useGetPaperAccountsWithDetails from "../api/hooks/useGetPaperAccountsWithDetails";
import type { Account, PaperAccount, Product } from "../api/rest/account";
import {
  ACCOUNT_POLLING_INTERVAL,
  PAPER_ACCOUNTS_POLLING_INTERVAL,
} from "../constants";
import {
  type AccountSelectionSyncEnvironment,
  useSynchronizedAccountSelection,
} from "./useSynchronizedAccountSelection";

export interface AccountContextProps {
  liveAccounts: Account[];
  paperAccounts: Account[];
  paperAccountsNoDetail: PaperAccount[];
  refetchCurrentAccount: () => void;
  refetchLiveAccounts: () => void;
  refetchPaperAccounts: () => void;
  currentAccount?: Account;
  setCurrentAccount: (accountId: string) => void;
  product?: Product;
  email?: string;
  isLoading: boolean;
}

const ACCOUNT_CONTEXT_DEFAULTS: AccountContextProps = {
  liveAccounts: [],
  paperAccounts: [],
  paperAccountsNoDetail: [],
  refetchCurrentAccount: () => null,
  refetchLiveAccounts: () => null,
  refetchPaperAccounts: () => null,
  currentAccount: undefined,
  setCurrentAccount: () => null,
  product: undefined,
  email: undefined,
  isLoading: true,
};

export const AccountContext = createContext<AccountContextProps>(
  ACCOUNT_CONTEXT_DEFAULTS,
);

interface AccountProviderProps {
  children: ReactNode;
  syncEnvironment?: AccountSelectionSyncEnvironment;
}

const byAccountId = (first: Account, second: Account) =>
  first.id.localeCompare(second.id);

type UnavailableSelectionAction =
  | "clear"
  | "local-default"
  | "pending"
  | "preserve"
  | "publish-default";

const getUnavailableSelectionAction = ({
  selectedAccountId,
  provisionalAccountId,
  resolvedAccountId,
  hasInitializedAccount,
  accountQueriesSucceeded,
  hasDefaultAccount,
}: {
  selectedAccountId: string;
  provisionalAccountId?: string;
  resolvedAccountId?: string;
  hasInitializedAccount: boolean;
  accountQueriesSucceeded: boolean;
  hasDefaultAccount: boolean;
}): UnavailableSelectionAction => {
  if (provisionalAccountId === selectedAccountId) {
    return accountQueriesSucceeded ? "publish-default" : "local-default";
  }
  if (resolvedAccountId === selectedAccountId) {
    if (!accountQueriesSucceeded) {
      return "preserve";
    }
    return hasDefaultAccount ? "publish-default" : "clear";
  }
  if (hasInitializedAccount) {
    return "pending";
  }
  return accountQueriesSucceeded ? "publish-default" : "local-default";
};

const AccountProvider = ({
  children,
  syncEnvironment,
}: AccountProviderProps) => {
  const { isAuthenticated } = useAuthContext();
  const [currentAccount, setCurrentAccount] = useState<Account>();
  const hasInitializedAccount = useRef(false);
  const resolvedAccountId = useRef<string>();
  const provisionalAccountId = useRef<string>();

  const {
    selectedAccountId,
    setSelectedAccountId,
    isLoading: isSavedAccountIdLoading,
  } = useSynchronizedAccountSelection(syncEnvironment);

  const {
    data: liveAccounts,
    refetch: refetchLiveAccounts,
    isLoading: isLiveAccountsLoading,
    error: liveAccountsError,
  } = useGetLiveAccounts({
    refetchInterval: ACCOUNT_POLLING_INTERVAL,
    enabled: isAuthenticated,
  });

  const {
    data: paperAccounts,
    paper_accounts: paperAccountsNoDetail,
    refetch: refetchPaperAccounts,
    isLoading: isPaperAccountsLoading,
    error: paperAccountsError,
  } = useGetPaperAccountsWithDetails({
    refetchInterval: PAPER_ACCOUNTS_POLLING_INTERVAL,
    enabled: isAuthenticated,
  });

  const isLoading =
    isLiveAccountsLoading || isPaperAccountsLoading || isSavedAccountIdLoading;

  // Memoized so the identity only changes when the query data does. These are
  // published through AccountContext, so an unmemoized `?? []` would hand every
  // consumer a new array on every render and force them to either re-run their
  // own hooks constantly or suppress the dependency.
  const safeLiveAccounts = useMemo(() => liveAccounts ?? [], [liveAccounts]);
  const safePaperAccounts = useMemo(() => paperAccounts ?? [], [paperAccounts]);
  const safePaperAccountsNoDetail = useMemo(
    () => paperAccountsNoDetail ?? [],
    [paperAccountsNoDetail],
  );

  const product: Product | undefined = useMemo(() => {
    if (!currentAccount) {
      return undefined;
    }
    if (safeLiveAccounts.some((account) => account.id === currentAccount.id)) {
      return "live";
    }
    if (safePaperAccounts.some((account) => account.id === currentAccount.id)) {
      return "paper";
    }
    return undefined;
  }, [currentAccount, safeLiveAccounts, safePaperAccounts]);

  const resolveExactAccount = useCallback(
    (accountId: string) =>
      safeLiveAccounts.find(
        (account) => account.id === accountId && !!account.account_number,
      ) ?? safePaperAccounts.find((account) => account.id === accountId),
    [safeLiveAccounts, safePaperAccounts],
  );

  const resolveDefaultAccount = useCallback(
    () =>
      [...safePaperAccounts].sort(byAccountId)[0] ??
      safeLiveAccounts
        .filter((account) => !!account.account_number)
        .sort(byAccountId)[0],
    [safeLiveAccounts, safePaperAccounts],
  );

  const reportMissingAccount = useCallback(
    (accountId: string) => {
      Sentry.captureMessage("AccountContext: No account found", {
        level: "warning",
        extra: {
          accountId,
          savedAccountId: selectedAccountId,
          loadingState: {
            isLoading,
            isLiveAccountsLoading,
            isPaperAccountsLoading,
            isSavedAccountIdLoading,
            isAuthenticated,
          },
          isAuthenticated,
          liveAccountIds: safeLiveAccounts.map((account) => account.id),
          paperAccountIds: safePaperAccounts.map((account) => account.id),
          queryErrors: {
            liveAccountsError,
            paperAccountsError,
          },
        },
      });
    },
    [
      isAuthenticated,
      isLiveAccountsLoading,
      isLoading,
      isPaperAccountsLoading,
      isSavedAccountIdLoading,
      liveAccountsError,
      paperAccountsError,
      safeLiveAccounts,
      safePaperAccounts,
      selectedAccountId,
    ],
  );

  const handleSetCurrentAccount = useCallback(
    (accountId: string) => {
      const account = resolveExactAccount(accountId);
      if (!account) {
        if (!isLoading) {
          reportMissingAccount(accountId);
        }
        return;
      }

      hasInitializedAccount.current = true;
      resolvedAccountId.current = account.id;
      provisionalAccountId.current = undefined;
      setCurrentAccount(account);
      setSelectedAccountId(account.id);
    },
    [
      isLoading,
      reportMissingAccount,
      resolveExactAccount,
      setSelectedAccountId,
    ],
  );

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    if (selectedAccountId !== null) {
      const exactAccount = resolveExactAccount(selectedAccountId);
      if (exactAccount) {
        hasInitializedAccount.current = true;
        resolvedAccountId.current = exactAccount.id;
        provisionalAccountId.current = undefined;
        setCurrentAccount((account) =>
          account === exactAccount ? account : exactAccount,
        );
        return;
      }

      const defaultAccount = resolveDefaultAccount();
      const accountQueriesSucceeded = !liveAccountsError && !paperAccountsError;
      const action = getUnavailableSelectionAction({
        selectedAccountId,
        provisionalAccountId: provisionalAccountId.current,
        resolvedAccountId: resolvedAccountId.current,
        hasInitializedAccount: hasInitializedAccount.current,
        accountQueriesSucceeded,
        hasDefaultAccount: !!defaultAccount,
      });

      switch (action) {
        case "preserve":
          return;
        case "clear":
        case "pending":
          setCurrentAccount(undefined);
          return;
        case "local-default":
          hasInitializedAccount.current = true;
          provisionalAccountId.current = selectedAccountId;
          if (defaultAccount) {
            setCurrentAccount((account) =>
              account === defaultAccount ? account : defaultAccount,
            );
          }
          return;
        case "publish-default":
          provisionalAccountId.current = undefined;
          if (!defaultAccount) {
            return;
          }
          hasInitializedAccount.current = true;
          resolvedAccountId.current = defaultAccount.id;
          setCurrentAccount(defaultAccount);
          setSelectedAccountId(defaultAccount.id);
          return;
      }
    }

    const defaultAccount = resolveDefaultAccount();
    if (!defaultAccount) {
      return;
    }

    hasInitializedAccount.current = true;
    resolvedAccountId.current = defaultAccount.id;
    provisionalAccountId.current = undefined;
    setCurrentAccount(defaultAccount);
    setSelectedAccountId(defaultAccount.id);
  }, [
    isAuthenticated,
    isLoading,
    liveAccountsError,
    paperAccountsError,
    resolveDefaultAccount,
    resolveExactAccount,
    selectedAccountId,
    setSelectedAccountId,
  ]);

  const handleRefetchCurrentAccount = useCallback(() => {
    switch (product) {
      case "paper":
        return refetchPaperAccounts();
      case "live":
        return refetchLiveAccounts();
    }
  }, [product, refetchLiveAccounts, refetchPaperAccounts]);

  return (
    <AccountContext.Provider
      value={{
        liveAccounts: safeLiveAccounts,
        paperAccounts: safePaperAccounts,
        paperAccountsNoDetail: safePaperAccountsNoDetail,
        refetchLiveAccounts,
        refetchPaperAccounts,
        refetchCurrentAccount: handleRefetchCurrentAccount,
        currentAccount,
        setCurrentAccount: handleSetCurrentAccount,
        product,
        isLoading,
        email: safeLiveAccounts[0]?.email,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

interface UseAccountContextOptions {
  liveOnly?: boolean;
}

export const useAccountContext = ({
  liveOnly,
}: UseAccountContextOptions = {}) => {
  const history = useHistory();

  const ctx = useContext(AccountContext);

  useEffect(() => {
    if (liveOnly && ctx.product === "paper") {
      history.replace("/");
    }
  }, [liveOnly, ctx.product, history]);

  if (liveOnly && ctx.product !== "live") {
    return {
      ...ctx,
      currentAccount: undefined,
    };
  }

  return ctx;
};

export default AccountProvider;
